/**
 * DndListProvider — single source of truth for DnD context in task lists.
 *
 * Wraps children with DndContext + DragOverlay so that ALL sortable views
 * (flat & grouped) share identical sensors, drag visuals, and haptics.
 *
 * **Two modes:**
 *
 * 1. **Flat** (default) — also renders a single SortableContext around
 *    children and handles reorder internally. Supports multi-drag when
 *    tasks are selected via Mod+Click / Shift+Click.
 *
 * 2. **Grouped** (`grouped=true`) — skips SortableContext (children render
 *    per-group contexts) and delegates drag events via callback props
 *    (`onGroupedDragOver`, `onGroupedDragEnd`). GroupedTaskList uses this
 *    mode to implement the multi-container DnD pattern.
 */

import {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { Capacitor } from "@capacitor/core"
import {
  DndContext,
  DragOverlay,
  closestCenter,
  MouseSensor,
  useSensor,
  useSensors,
  useDndContext,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type DragCancelEvent,
} from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useViewContext } from "@/app/stores/viewContext"
import {
  reorderIndex,
  distributeIndexes,
  indexNeedsRepair,
  nextIndex,
} from "@/shared/utils/list/indexUtils"
import {
  reorderTaskDual,
  computeGhostIndex,
  bulkReorderDual,
  getRealNeighborIndexes,
} from "@/db/operations"
import type { BulkReorderItem } from "@/db/operations"
import { ListDragSensor } from "./ListDragSensor"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { hapticLight, hapticMedium } from "@/shared/utils/platform/haptics"
import { handleSidebarDrop } from "./sidebarDropHandler"
import type { ViewItem } from "@/features/views/hooks/useViewData"
import { isOverSidebarRef, DND_SIDEBAR_EXIT_EVENT } from "./dndSidebarState"
import { contentZoomModifier } from "./zoomModifier"
import {
  getSectionBlockIds,
  getSectionBoundaryIds,
  isSectionItem,
} from "./sectionUtils"
import { ObjectType } from "@/db/schema"
import { buildDragSourceInfo } from "./dndConstraints"
import { useTagFilterStore } from "@/features/tags/tagFilterStore"
import { useViewFilterStore } from "@/features/views/stores/viewFilterStore"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DndListProviderProps {
  /** Flat display-ordered items — used for SortableContext IDs + reorder. */
  items: ViewItem[]
  /** Which index field to persist: "index" (container) or "startAtIndex" (date views) */
  indexField: "index" | "startAtIndex"
  /** Optimistic setter — splices the reordered array for instant feedback */
  setOptimistic: (
    updater:
      | ViewItem[]
      | ((prev: ViewItem[] | null) => ViewItem[] | null)
      | null,
  ) => void
  children: ReactNode

  // ── Grouped mode (multi-container) ────────────────────────────────────
  /**
   * When true, DndListProvider does NOT render a SortableContext; children
   * are expected to render per-group SortableContexts.
   * Event handling is delegated to the callback props below.
   */
  grouped?: boolean
  /**
   * When true, skip ghost-index writes during reorder (flat Today mode).
   * Only the primary index field is written — the "other" dimension is
   * left untouched so it doesn't disrupt ordering in the grouped view.
   * See master plan §4 "DnD i flat-läge".
   */
  skipGhostIndex?: boolean
  /** Called on DragOverEvent — parent handles cross-container transfers. */
  onGroupedDragOver?: (event: DragOverEvent) => void
  /** Called on DragEndEvent — parent persists final position.
   *  draggedIds is the set of multi-selected IDs (null = single drag). */
  onGroupedDragEnd?: (event: DragEndEvent, draggedIds: string[] | null) => void
}

// ---------------------------------------------------------------------------
// Post-drop repair helper — Layer 3
// ---------------------------------------------------------------------------

/**
 * If the precision guard in indexUtils fired (indexNeedsRepair === true),
 * schedule an async dimension-aware re-index so the next drag has clean spacing.
 * Non-blocking: runs after the optimistic update has already been applied.
 *
 * @param field - Which dimension was dragged: "index" (container) or "startAtIndex" (date views)
 * @param parentPath - Required for container dimension; ignored for startAtIndex
 */
function scheduleRepairIfNeeded(
  field: "index" | "startAtIndex",
  parentPath: string | undefined,
) {
  if (!indexNeedsRepair) return
  // Dynamic import to keep the repair module out of the critical path
  import("@/db/operations/indexRepair").then(async ({ repairDimension }) => {
    try {
      const result = await repairDimension(field, parentPath)
      if (result && result.updatedCount > 0) {
        console.log(
          `[DndListProvider] Post-drop repair (${field}): re-indexed ${result.updatedCount}/${result.itemCount} items in "${result.container}" (${result.durationMs.toFixed(1)}ms)`,
        )
      }
    } catch (e) {
      console.warn("[DndListProvider] Post-drop repair failed:", e)
    }
  })
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DndListProvider({
  items,
  indexField,
  setOptimistic,
  children,
  grouped,
  skipGhostIndex,
  onGroupedDragOver,
  onGroupedDragEnd,
}: DndListProviderProps) {
  const setDragging = useViewContext((s) => s.setDragging)
  const clearSelection = useViewContext((s) => s.clearSelection)
  const [activeId, setActiveId] = useState<string | null>(null)

  // Track which IDs are being dragged (for multi-drag)
  // null = no drag; string[] = IDs in visual order
  const draggedIdsRef = useRef<string[] | null>(null)
  // §19: Track whether current drag is a section block drag
  const isSectionDragRef = useRef(false)
  // §19: Track computed section drop target separately — collision returns []
  // during section drag to suppress sortable transforms, so event.over is null.
  // This ref stores the boundary target computed by multiDragCollision.
  const sectionOverRef = useRef<string | null>(null)
  // Stable ref for items — used in collision callback to avoid deps
  const itemsRef = useRef(items)
  itemsRef.current = items

  // isOverSidebarRef is a module-level singleton (dndSidebarState.ts).
  // Managed by useSidebarDragOver (sets on pointer events) and cleared
  // by DroppableRectRefresher after rect re-measurement.
  // This avoids routing sidebar state through Zustand/React, eliminating
  // re-renders of the entire DnD tree on every sidebar enter/exit.

  // ── Collision detection ──────────────────────────────────────────────────
  // Custom wrapper around closestCenter that:
  // 1. Returns EMPTY collisions when the pointer is over the sidebar.
  //    This completely disables sortable placeholder movement in the main
  //    list so the user isn't confused by rows shuffling while they aim at
  //    a sidebar menu target.
  // 2. Excludes multi-drag companions from collision candidates so "over"
  //    is always a real visible item or a group header/container.
  const multiDragCollision: typeof closestCenter = useCallback((args) => {
    // Over sidebar → no collisions → sortable transforms reset to identity
    if (isOverSidebarRef.current) return []

    // Over a non-sidebar drop zone (e.g. EveningSection) — sidebarDropTarget
    // is set by useSidebarDragOver, but isOverSidebarRef stays false because
    // the zone is in the main content area. Suppress collisions here too so
    // the sortable list doesn't show insertion indicators.
    if (useViewContext.getState().sidebarDropTarget) return []

    // §19 Section boundary snapping: filter collision candidates to
    // section boundaries only (before each section, top/bottom) and
    // return actual results so @dnd-kit computes transforms natively.
    // Companions (children) collapse via CSS data-drag-companion, while
    // remaining items shift apart to show a natural gap at the drop target.
    // handleDragEnd reads sectionOverRef for the actual drop target.
    if (isSectionDragRef.current) {
      const companions = draggedIdsRef.current
      const draggedSet = new Set(companions ?? [])
      const boundaryIds = getSectionBoundaryIds(itemsRef.current, draggedSet)
      // Include active section for "return to sender" (cancel by drop)
      boundaryIds.add(args.active.id as string)
      const filtered = args.droppableContainers.filter((c) =>
        boundaryIds.has(c.id as string),
      )
      const results = closestCenter({ ...args, droppableContainers: filtered })
      const targetId = results.length > 0 ? (results[0]!.id as string) : null
      sectionOverRef.current = targetId

      // §19 Forward-drag gap correction:
      // In a forward drag (active before over) @dnd-kit shifts items UP
      // through the "over" target, placing the visual gap AFTER it.
      // For section drags we always insert BEFORE the target section,
      // so the gap should appear BEFORE it.
      //
      // Strategy: return a predecessor item (last non-dragged item before
      // the target) so @dnd-kit opens the gap one position earlier.
      //
      // Adjacent-forward case (no non-dragged items between active block
      // and target): can't place a gap correctly without @dnd-kit pulling
      // the section past its own children. Return active ID instead
      // (visual no-op / return-to-sender) — sectionOverRef still has the
      // real target so the drop works. The user sees faded source + overlay.
      if (targetId && targetId !== (args.active.id as string)) {
        const all = itemsRef.current
        const activeIdx = all.findIndex(
          (vi) => vi.task.id === (args.active.id as string),
        )
        const overIdx = all.findIndex((vi) => vi.task.id === targetId)
        const isForward = activeIdx < overIdx

        if (isForward) {
          const remaining = all.filter(
            (vi) => !draggedSet.has(vi.task.id ?? ""),
          )
          const rIdx = remaining.findIndex((vi) => vi.task.id === targetId)

          // §19-fix: When the target is the LAST remaining item and NOT
          // a section, return results directly. @dnd-kit's forward-drag
          // naturally places the gap AFTER the over target — which is
          // exactly right for "drop at bottom of list". handleDragEnd's
          // isLast branch inserts AFTER this item, so visuals match logic.
          const isLastRemaining = rIdx === remaining.length - 1
          const targetItem = remaining[rIdx]
          const targetIsSection = targetItem?.task.type === ObjectType.Section
          if (isLastRemaining && !targetIsSection) {
            return results
          }

          if (rIdx > 0) {
            const predecessorId = remaining[rIdx - 1]!.task.id ?? ""
            const predFullIdx = all.findIndex(
              (vi) => vi.task.id === predecessorId,
            )
            if (predFullIdx > activeIdx) {
              // Far-forward: predecessor between active and target → gap before target
              return [{ id: predecessorId, data: results[0]!.data }]
            }
          }
          // Adjacent-forward: return active ID → no transforms, but drop works
          return [{ id: args.active.id as string, data: results[0]!.data }]
        }
      }
      return results
    }

    const companions = draggedIdsRef.current
    if (!companions || companions.length <= 1) {
      return closestCenter(args)
    }
    const activeId = args.active.id
    const companionSet = new Set(companions)
    const filtered = args.droppableContainers.filter(
      (c) => c.id === activeId || !companionSet.has(c.id as string),
    )
    return closestCenter({ ...args, droppableContainers: filtered })
  }, [])

  // ── Sensors ──────────────────────────────────────────────────────────────
  // Keyboard sorting removed in favour of ↑↓ arrow navigation + Shift range
  // selection in useKeyboardShortcuts (matches Things 3 / Linear pattern).
  //
  // CRITICAL FIX for macOS Capacitor: Desktop apps must use MouseSensor only.
  // ListDragSensor uses TouchEvents which bypass auto-scroll in DnD Kit.
  // Since macOS desktop is cursor-driven (trackpad/mouse), MouseSensor is correct.
  // Mobile (iOS/Android) keeps both sensors for native touch + fallback.
  //
  // macOS WKWebView scroll-delta fix: patchWindowScrollForMacOS() inverts scroll
  // when dragging, so we can now enable autoScroll on all platforms uniformly.
  //
  // Options are memoized to prevent useSensor from returning a new object on
  // every render. @dnd-kit v6 includes `options` in useMemo deps — without
  // stable refs, useCombineActivators regenerates activators every cycle,
  // causing unnecessary re-binds and potential stale-handler issues.
  const mouseOpts = useMemo(
    () => ({ activationConstraint: { distance: 8 } }),
    [],
  )
  const touchOpts = useMemo(
    () => ({ activationConstraint: { delay: 250, tolerance: 3 } }),
    [],
  )

  // On macOS, use only MouseSensor (supports auto-scroll).
  // On mobile, use both MouseSensor and ListDragSensor.
  const isMacOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "macOS"
  const sensors = useSensors(
    useSensor(MouseSensor, mouseOpts),
    ...(isMacOS ? [] : [useSensor(ListDragSensor, touchOpts)]),
  )

  // ── Item IDs (stable ref for SortableContext) ───────────────────────────
  const taskIds = useMemo(
    () => items.map((vi) => vi.task.id ?? "no-id"),
    [items],
  )

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = event.active.id as string
      setActiveId(id)
      hapticLight()

      // §19 Section auto-expand: if dragged item is a section,
      // always drag it as a block with all positional children.
      const draggedItem = items.find((vi) => vi.task.id === id)
      if (draggedItem && isSectionItem(draggedItem)) {
        isSectionDragRef.current = true
        const blockIds = getSectionBlockIds(id, items)
        draggedIdsRef.current = blockIds

        // §33 Constraint info: section block drag
        const blockTypes = blockIds.map((bid) => {
          const vi = items.find((v) => v.task.id === bid)
          return vi?.task.type ?? ObjectType.Task
        })
        setDragging(true, id, buildDragSourceInfo(blockTypes))

        // Only use multi-select if the section was already explicitly
        // selected (via swipe or menu). Never auto-select on drag start.
        const currentSelected = useViewContext.getState().selectedTaskIds
        const currentSet = new Set(currentSelected)
        const sectionIsSelected = currentSet.has(id)

        if (sectionIsSelected && currentSelected.length > blockIds.length) {
          // Multi-section drag: use the full selection in visual order
          const orderedIds = items
            .filter((vi) => currentSelected.includes(vi.task.id ?? ""))
            .map((vi) => vi.task.id ?? "")
          draggedIdsRef.current = orderedIds
        }

        // Publish companion IDs so SortableTaskRow can dim them
        const companions = (draggedIdsRef.current ?? blockIds).filter((bid) => bid !== id)
        useViewContext.setState({ dragCompanionIds: companions })
        return
      }

      isSectionDragRef.current = false

      // Read selection imperatively — avoids subscribing to selectedTaskIds
      // which would re-render the entire DnD tree on every selection change.
      const selectedTaskIds = useViewContext.getState().selectedTaskIds

      // Multi-drag: if dragged task is selected, drag ALL selected in visual order
      // Otherwise, clear selection and drag single
      if (selectedTaskIds.includes(id) && selectedTaskIds.length > 1) {
        // Keep visual order from items array
        const orderedIds = items
          .filter((vi) => selectedTaskIds.includes(vi.task.id ?? ""))
          .map((vi) => vi.task.id ?? "")
        draggedIdsRef.current = orderedIds

        // §33 Constraint info: multi-drag types
        const types = orderedIds.map((oid) => {
          const vi = items.find((v) => v.task.id === oid)
          return vi?.task.type ?? ObjectType.Task
        })
        setDragging(true, id, buildDragSourceInfo(types))

        // Publish companion IDs for multi-drag visual feedback
        const companions = orderedIds.filter((oid) => oid !== id)
        useViewContext.setState({ dragCompanionIds: companions })
      } else {
        // Single drag: do NOT set selectedTaskIds — multiselect should only
        // activate via explicit user action (swipe R→L on mobile, Mod+Click/Shift on desktop).
        // The drag overlay provides visual feedback; the original row is hidden by useSortable.
        draggedIdsRef.current = null // single drag

        // §33 Constraint info: single drag type
        const sourceType = draggedItem?.task.type ?? ObjectType.Task
        setDragging(true, id, buildDragSourceInfo([sourceType]))
      }
    },
    [setDragging, items, clearSelection],
  )

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active } = event
      const draggedIds = draggedIdsRef.current
      const wasSectionDrag = isSectionDragRef.current

      // §21: Detect if any filter is active — if so, use real-neighbor
      // index lookup instead of visible-neighbor midpoint.
      const isFilterActive =
        useTagFilterStore.getState().isFiltering() ||
        useViewFilterStore.getState().isFiltering()

      // §19: sectionOverRef tracks the boundary-filtered target;
      // for regular drags use event.over directly.
      const overId = wasSectionDrag
        ? (sectionOverRef.current ?? (event.over?.id as string | undefined))
        : (event.over?.id as string | undefined)

      // Read sidebar target BEFORE setDragging(false) — the hook's cleanup
      // effect will clear it when isDragging transitions to false.
      const sidebarTarget = useViewContext.getState().sidebarDropTarget

      setActiveId(null)
      setDragging(false)
      draggedIdsRef.current = null
      isSectionDragRef.current = false
      sectionOverRef.current = null

      // No-op: dropped back on same position (only when NOT a sidebar drop)
      if (!sidebarTarget && overId && String(active.id) === String(overId)) {
        // If this was a multi-drag attempt, clear selection so DnD state
        // doesn't linger and block future sensor activations.
        if (draggedIds && draggedIds.length > 1) clearSelection()
        return
      }

      // ── Sidebar drop target (PRIORITY) ─────────────────────────────
      // Sidebar drops move tasks to a different view → clear selection.
      // Sidebar target takes precedence over sortable "over" — closestCenter
      // always finds a sortable target even when the pointer is over the
      // sidebar, so we check sidebarTarget first.
      if (sidebarTarget) {
        const taskIds = draggedIds ?? [active.id as string]
        const taskIdSet = new Set(taskIds)

        // Sidebar drops move tasks away → clear selection
        clearSelection()

        // Optimistic: remove dragged tasks from view immediately so there's
        // no flash of the task re-appearing before the DB write completes.
        setOptimistic(items.filter((vi) => !taskIdSet.has(vi.task.id ?? "")))

        hapticMedium()
        await handleSidebarDrop(sidebarTarget, taskIds)
        return
      }

      if (!overId || String(active.id) === String(overId)) {
        // No valid drop target found — clear multi-drag selection
        if (draggedIds && draggedIds.length > 1) clearSelection()
        return
      }

      // ── §19 Section block drop ────────────────────────────────────
      // Section-boundary insertion: always insert BEFORE a section target
      // (representing the slot above that section). This prevents "stealing"
      // children from the target section.
      if (wasSectionDrag && draggedIds && draggedIds.length > 0) {
        const draggedSet = new Set(draggedIds)
        const remaining = items.filter(
          (vi) => !draggedSet.has(vi.task.id ?? ""),
        )
        const overInRemaining = remaining.findIndex(
          (vi) => vi.task.id === overId,
        )
        if (overInRemaining === -1) {
          return
        }

        hapticMedium()

        const overItem = remaining[overInRemaining]!
        const isOverSection = overItem.task.type === ObjectType.Section
        const isLast = overInRemaining === remaining.length - 1

        // Determine insertion point based on boundary semantics:
        // - Over a section → insert BEFORE it (take slot above)
        // - Over first item → insert BEFORE (top of list)
        // - Over last non-section item → insert AFTER (bottom of list)
        let prevItem: ViewItem | undefined
        let nextItem: ViewItem | undefined
        let insertAt: number

        if (isOverSection || overInRemaining === 0) {
          // Insert BEFORE this item
          prevItem =
            overInRemaining > 0 ? remaining[overInRemaining - 1] : undefined
          nextItem = overItem
          insertAt = overInRemaining
        } else if (isLast) {
          // Insert AFTER last item (bottom of list)
          prevItem = overItem
          nextItem = undefined
          insertAt = overInRemaining + 1
        } else {
          // Fallback: insert BEFORE
          prevItem =
            overInRemaining > 0 ? remaining[overInRemaining - 1] : undefined
          nextItem = overItem
          insertAt = overInRemaining
        }

        let prevIdx = prevItem?.sortIndex
        let nextIdx = nextItem?.sortIndex

        // §21: Real-neighbor lookup when filter hides tasks
        if (isFilterActive) {
          const firstDragged = items.find((vi) => vi.task.id === draggedIds[0])
          const { realPrev, realNext } = await getRealNeighborIndexes(
            indexField,
            prevIdx,
            nextIdx,
            draggedSet,
            indexField === "index" ? firstDragged?.task.parentPath : undefined,
          )
          prevIdx = realPrev
          nextIdx = realNext
        }

        const newIndexes = distributeIndexes(
          prevIdx,
          nextIdx,
          draggedIds.length,
        )

        const draggedItems = draggedIds
          .map((id) => items.find((vi) => vi.task.id === id))
          .filter(Boolean) as ViewItem[]

        const reordered = [...remaining]
        draggedItems.forEach((item, i) => {
          reordered.splice(insertAt + i, 0, {
            ...item,
            sortIndex: newIndexes[i]!,
          })
        })
        setOptimistic(reordered)

        try {
          if (skipGhostIndex) {
            const { reorderTask } = await import("@/db/operations")
            for (let i = 0; i < draggedIds.length; i++) {
              await reorderTask(draggedIds[i]!, newIndexes[i]!, indexField)
            }
          } else {
            const ghostField: "index" | "startAtIndex" =
              indexField === "startAtIndex" ? "index" : "startAtIndex"

            const bulkItems: BulkReorderItem[] = await Promise.all(
              draggedIds.map(async (id, i) => {
                const vi = draggedItems[i]!
                const ghostIdx = await computeGhostIndex(
                  ghostField,
                  ghostField === "index" ? vi.task.parentPath : undefined,
                )
                return {
                  id,
                  primaryIndex: newIndexes[i]!,
                  ghostIndex: ghostIdx,
                }
              }),
            )

            await bulkReorderDual(bulkItems, indexField, ghostField)
          }
          // Layer 3: post-drop repair if precision guard fired
          scheduleRepairIfNeeded(indexField, draggedItems[0]?.task.parentPath)
        } catch (error) {
          console.error(
            "[DndListProvider] Section block reorder failed:",
            error,
          )
          setOptimistic(null)
        }
        // Clear selection after section block drop
        clearSelection()
        return
      }

      // ── Multi-drag ─────────────────────────────────────────────────
      if (draggedIds && draggedIds.length > 1) {
        const draggedSet = new Set(draggedIds)
        const overIndex = items.findIndex((vi) => vi.task.id === overId)
        if (overIndex === -1) return

        hapticMedium()

        // Determine drag direction: compare the ACTIVE (physically dragged)
        // item's position with the over target's position.
        // Using active.id (not draggedIds[0]) because draggedIds is ordered
        // by visual position — draggedIds[0] may be a different item than
        // what the user actually picked up, causing wrong direction when
        // the active item is not first in the selection.
        const activeIndex = items.findIndex(
          (vi) => vi.task.id === (active.id as string),
        )
        const isDraggingForward = activeIndex < overIndex

        // Remove dragged items, keeping the rest in order
        const remaining = items.filter(
          (vi) => !draggedSet.has(vi.task.id ?? ""),
        )
        // Find the over item position in the remaining list
        const overInRemaining = remaining.findIndex(
          (vi) => vi.task.id === overId,
        )
        if (overInRemaining === -1) return

        // Compute insertion point: BEFORE or AFTER the over target
        // depending on drag direction
        let prevItem: ViewItem | undefined
        let nextItem: ViewItem | undefined
        let insertAt: number

        if (isDraggingForward) {
          // Dragging forward → insert AFTER the over target
          prevItem = remaining[overInRemaining]
          nextItem =
            overInRemaining < remaining.length - 1
              ? remaining[overInRemaining + 1]
              : undefined
          insertAt = overInRemaining + 1
        } else {
          // Dragging backward → insert BEFORE the over target
          prevItem =
            overInRemaining > 0 ? remaining[overInRemaining - 1] : undefined
          nextItem = remaining[overInRemaining]
          insertAt = overInRemaining
        }

        let prevIdx = prevItem?.sortIndex
        let nextIdx = nextItem?.sortIndex

        // §21: Real-neighbor lookup when filter hides tasks
        if (isFilterActive) {
          const dragSet = new Set(draggedIds)
          const firstDragged = items.find((vi) => vi.task.id === draggedIds[0])
          const { realPrev, realNext } = await getRealNeighborIndexes(
            indexField,
            prevIdx,
            nextIdx,
            dragSet,
            indexField === "index" ? firstDragged?.task.parentPath : undefined,
          )
          prevIdx = realPrev
          nextIdx = realNext
        }

        const newIndexes = distributeIndexes(
          prevIdx,
          nextIdx,
          draggedIds.length,
        )

        // Get dragged items in visual order
        const draggedItems = draggedIds
          .map((id) => items.find((vi) => vi.task.id === id))
          .filter(Boolean) as ViewItem[]

        // Optimistic: splice all dragged items at the drop position
        const reordered = [...remaining]
        draggedItems.forEach((item, i) => {
          reordered.splice(insertAt + i, 0, {
            ...item,
            sortIndex: newIndexes[i]!,
          })
        })
        setOptimistic(reordered)

        // Persist via bulkReorderDual (or single-field when skipGhostIndex)
        try {
          if (skipGhostIndex) {
            // Flat Today mode: write only primary index, skip ghost
            const { reorderTask } = await import("@/db/operations")
            for (let i = 0; i < draggedIds.length; i++) {
              await reorderTask(draggedIds[i]!, newIndexes[i]!, indexField)
            }
          } else {
            const ghostField: "index" | "startAtIndex" =
              indexField === "startAtIndex" ? "index" : "startAtIndex"

            const bulkItems: BulkReorderItem[] = await Promise.all(
              draggedIds.map(async (id, i) => {
                const vi = draggedItems[i]!
                const ghostIdx = await computeGhostIndex(
                  ghostField,
                  ghostField === "index" ? vi.task.parentPath : undefined,
                )
                return {
                  id,
                  primaryIndex: newIndexes[i]!,
                  ghostIndex: ghostIdx,
                }
              }),
            )

            await bulkReorderDual(bulkItems, indexField, ghostField)
          }
          // Layer 3: post-drop repair if precision guard fired
          scheduleRepairIfNeeded(indexField, draggedItems[0]?.task.parentPath)
        } catch (error) {
          console.error("[DndListProvider] Multi-drag reorder failed:", error)
          setOptimistic(null) // rollback
        }
        // Clear selection after multi-drag drop
        clearSelection()
        return
      }

      // ── Single-drag ────────────────────────────────────────────────
      const sourceIndex = items.findIndex((vi) => vi.task.id === active.id)
      const overIndex = items.findIndex((vi) => vi.task.id === overId)
      if (sourceIndex === -1 || overIndex === -1) return

      // No-op: same position after resolving indices
      if (sourceIndex === overIndex) return

      // Haptic feedback on successful drop
      hapticMedium()

      // Calculate new fractional index using sortIndex (correct per view)
      let newIdx = reorderIndex(
        items,
        sourceIndex,
        overIndex,
        (vi) => vi.sortIndex,
      )

      // §21: When filter is active, recalculate with real DB neighbors
      if (isFilterActive) {
        const without = items.filter((_, i) => i !== sourceIndex)
        const toIndex = overIndex > sourceIndex ? overIndex - 1 : overIndex
        const prevVis =
          toIndex > 0 ? without[toIndex - 1]?.sortIndex : undefined
        const nextVis =
          without[toIndex] != null ? without[toIndex]!.sortIndex : undefined
        const dragSet = new Set([active.id as string])
        const task = items[sourceIndex]!.task
        const { realPrev, realNext } = await getRealNeighborIndexes(
          indexField,
          prevVis,
          nextVis,
          dragSet,
          indexField === "index" ? task.parentPath : undefined,
        )
        newIdx = nextIndex(realPrev, realNext)
      }

      // Optimistic: splice array immediately with updated sortIndex
      const reordered = Array.from(items)
      const [moved] = reordered.splice(sourceIndex, 1)
      if (!moved) return
      reordered.splice(overIndex, 0, {
        ...moved,
        sortIndex: newIdx,
      })
      setOptimistic(reordered)

      // Persist index write (dual or single depending on skipGhostIndex)
      const task = moved.task
      const taskId = task.id
      if (taskId) {
        try {
          if (skipGhostIndex) {
            // Flat Today mode: write only primary index, skip ghost
            const { reorderTask } = await import("@/db/operations")
            await reorderTask(taskId, newIdx, indexField)
          } else {
            // Determine ghost field (the "other" dimension)
            const ghostField: "index" | "startAtIndex" =
              indexField === "startAtIndex" ? "index" : "startAtIndex"

            // Compute ghost index — direct Dexie query (~1-5ms)
            const ghostIdx = await computeGhostIndex(
              ghostField,
              ghostField === "index" ? task.parentPath : undefined,
            )

            // Write both in a single transaction → 1 Dexie notification
            await reorderTaskDual(
              taskId,
              newIdx,
              indexField,
              ghostIdx,
              ghostField,
            )
          }
          // Layer 3: post-drop repair if precision guard fired
          scheduleRepairIfNeeded(indexField, task.parentPath)
        } catch (error) {
          console.error("[DndListProvider] Reorder failed:", error)
          setOptimistic(null) // rollback
        }
      }
      // Clear any selection after single-drag drop
      clearSelection()
    },
    [
      items,
      indexField,
      setOptimistic,
      setDragging,
      skipGhostIndex,
      clearSelection,
    ],
  )

  // ── Drag cancel handler ─────────────────────────────────────────────────
  // @dnd-kit fires onDragCancel (NOT onDragEnd) when the pointer is outside
  // all droppable zones. Since the sidebar is outside DndContext, dropping
  // onto sidebar items always triggers cancel. We intercept it here.
  const handleDragCancel = useCallback(
    async (event: DragCancelEvent) => {
      const sidebarTarget = useViewContext.getState().sidebarDropTarget
      const draggedIds = draggedIdsRef.current

      setActiveId(null)
      setDragging(false)
      draggedIdsRef.current = null
      isSectionDragRef.current = false
      sectionOverRef.current = null

      // Always clear drag-induced selection
      clearSelection()

      if (sidebarTarget) {
        const taskIds = draggedIds ?? [event.active.id as string]
        const taskIdSet = new Set(taskIds)

        // Optimistic: remove dragged tasks from view immediately
        setOptimistic(items.filter((vi) => !taskIdSet.has(vi.task.id ?? "")))

        hapticMedium()
        await handleSidebarDrop(sidebarTarget, taskIds)
      }
    },
    [items, setOptimistic, setDragging, clearSelection],
  )

  // ── Active item for DragOverlay ─────────────────────────────────────────
  const activeItem = activeId
    ? items.find((vi) => vi.task.id === activeId)
    : undefined

  // Compute isFirstInGroup for the active item so the overlay reproduces the
  // same top margin as the original element. Without this, section drag
  // overlays jump up because dnd-kit measures the sortable wrapper (which
  // includes the margin) but the overlay renders without it.
  const activeIsFirstInGroup = useMemo(() => {
    if (!activeItem) return true
    const idx = items.indexOf(activeItem)
    if (idx <= 0) return true
    const prev = items[idx - 1]
    return prev?.task.type === ObjectType.Section
  }, [activeItem, items])

  // Multi-drag count for overlay badge
  // §19: Section drags are block drags — show total count (section + children).
  const multiDragCount = draggedIdsRef.current?.length ?? 0
  const showMultiDragBadge = multiDragCount > 1

  // ── Overlay content ─────────────────────────────────────────────────────
  // §15: When pointer is over the sidebar, we show a compact pill indicator
  // (Things 3 model) attached directly to the cursor via a fixed-position
  // portal — this bypasses DragOverlay's transform which maintains the
  // grab-offset and would place the pill far from the cursor.
  // When NOT over the sidebar, DragOverlay shows the full TaskItem clone.

  // Full TaskItem overlay (shown when dragging within the main list)
  const dragOverlayContent = activeItem ? (
    <div className="pointer-events-none relative opacity-85">
      <TaskItem
        task={activeItem.task}
        isDragOverlay
        isFirstInGroup={activeIsFirstInGroup}
        startAtData={activeItem.startAtData}
        onComplete={() => {}}
        onUncomplete={() => {}}
      />
      {showMultiDragBadge && (
        <span
          className={`absolute -right-1 z-10 flex size-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground shadow-md ${
            activeItem.task.type === ObjectType.Section && !activeIsFirstInGroup
              ? "top-[26px]"
              : "-top-2.5"
          }`}
        >
          {multiDragCount}
        </span>
      )}
    </div>
  ) : null

  // Cursor-attached pill (rendered via portal, positioned at pointer).
  // Uses a ref + useEffect with pointermove for zero-React-render positioning.
  // Always rendered during drag; CSS (body.dnd-over-sidebar) toggles visibility.
  const pillRef = useRef<HTMLDivElement>(null)

  // Pill positioning — runs throughout drag, CSS shows/hides the pill.
  // Cost: two style property writes per pointermove (negligible).
  useEffect(() => {
    if (!activeId) return
    const el = pillRef.current
    if (!el) return

    const onMove = (e: PointerEvent) => {
      el.style.left = `${e.clientX}px`
      el.style.top = `${e.clientY}px`
    }
    document.addEventListener("pointermove", onMove, true)
    return () => document.removeEventListener("pointermove", onMove, true)
  }, [activeId])

  const pillCount = Math.min(multiDragCount || 1, 3)

  // Effective count for the pill badge
  const pillBadgeCount = multiDragCount < 1 ? 1 : multiDragCount

  // Pill portal — always mounted during drag, visibility driven by CSS.
  // body.dnd-over-sidebar .dnd-sidebar-pill { visibility: visible }
  const sidebarPill = activeId
    ? createPortal(
        <div
          ref={pillRef}
          className="dnd-sidebar-pill pointer-events-none invisible fixed z-9999"
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <div className="relative h-7 w-14">
            {/* Stacked pills — bottom to top so topmost renders last */}
            {Array.from({ length: pillCount })
              .map((_, i) => {
                const stackIndex = pillCount - 1 - i // 0 = bottom, pillCount-1 = top
                const offset = stackIndex * 3 // 3px between each
                const opacity =
                  stackIndex === 0 ? 1 : stackIndex === 1 ? 0.6 : 0.35
                return (
                  <div
                    key={i}
                    className="absolute inset-x-0 h-7 rounded-md bg-primary shadow-md"
                    style={{
                      height: "18px",
                      top: offset,
                      left: offset,
                      opacity,
                    }}
                  />
                )
              })
              .reverse()}
            {multiDragCount > -1 && (
              <span className="absolute -right-1 -top-2.5 z-10 scale-75 flex size-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground shadow-md">
                {pillBadgeCount}
              </span>
            )}
          </div>
        </div>,
        document.body,
      )
    : null

  // ── Grouped mode: delegate onDragOver + onDragEnd to parent ───────────
  if (grouped) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={multiDragCollision}
        onDragStart={handleDragStart}
        onDragOver={onGroupedDragOver}
        onDragEnd={(e) => {
          const draggedIds = draggedIdsRef.current

          // Read sidebar target BEFORE clearing drag state
          const sidebarTarget = useViewContext.getState().sidebarDropTarget

          draggedIdsRef.current = null
          isSectionDragRef.current = false
          sectionOverRef.current = null
          setActiveId(null)
          setDragging(false)

          // Check sidebar drop target FIRST — takes priority over sortable over
          if (sidebarTarget) {
            const taskIds = draggedIds ?? [e.active.id as string]
            const taskIdSet = new Set(taskIds)

            // Sidebar drops move tasks away → clear selection
            clearSelection()

            // Optimistic removal from view
            setOptimistic(
              items.filter((vi) => !taskIdSet.has(vi.task.id ?? "")),
            )

            hapticMedium()
            handleSidebarDrop(sidebarTarget, taskIds)
            return
          }

          onGroupedDragEnd?.(e, draggedIds)
          // Clear selection after grouped drag drop
          clearSelection()
        }}
        onDragCancel={handleDragCancel}
        autoScroll={{ enabled: true }}
      >
        <DroppableRectRefresher />
        {children}
        <DragOverlay dropAnimation={null} modifiers={[contentZoomModifier]}>
          <div className="dnd-overlay-content">{dragOverlayContent}</div>
        </DragOverlay>
        {sidebarPill}
      </DndContext>
    )
  }

  // ── Flat mode: single SortableContext + built-in reorder ───────────────
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={multiDragCollision}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{ enabled: true }}
    >
      <DroppableRectRefresher />
      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
      <DragOverlay dropAnimation={null} modifiers={[contentZoomModifier]}>
        <div className="dnd-overlay-content">{dragOverlayContent}</div>
      </DragOverlay>
      {sidebarPill}
    </DndContext>
  )
}

// ---------------------------------------------------------------------------
// DroppableRectRefresher — forces @dnd-kit to re-measure droppable rects
// ---------------------------------------------------------------------------
/**
 * Rendered inside DndContext.  When the pointer returns from the sidebar
 * to the main list during a drag, CSS collapse (max-height:0) has altered
 * element heights.  @dnd-kit's default MeasuringStrategy only measures
 * rects on drag start / item change, so cached rects are stale.
 *
 * Listens for the `DND_SIDEBAR_EXIT_EVENT` custom event (dispatched by
 * useSidebarDragOver) instead of subscribing to Zustand — this avoids
 * React re-renders entirely.
 *
 * Flow:
 * 1. useSidebarDragOver detects pointer left sidebar → dispatches event
 * 2. This component waits for expand animation to finish (230ms)
 * 3. Removes the `dnd-expanding` class, measures droppable rects
 * 4. Clears isOverSidebarRef so collision detection resumes
 */
function DroppableRectRefresher() {
  const ctx = useDndContext()
  const ctxRef = useRef(ctx)
  ctxRef.current = ctx

  useEffect(() => {
    let expandTimer: ReturnType<typeof setTimeout> | null = null

    const handleSidebarExit = () => {
      // Wait for the CSS expand animation to finish (200ms transition + buffer)
      // before measuring rects and resuming collision detection.
      if (expandTimer) clearTimeout(expandTimer)
      expandTimer = setTimeout(() => {
        // Re-entered sidebar during animation? Body class will be present.
        if (document.body.classList.contains("dnd-over-sidebar")) return

        document.body.classList.remove("dnd-expanding")
        // Schedule measurement after one rAF so layout is fully settled.
        requestAnimationFrame(() => {
          const { measureDroppableContainers, droppableContainers } =
            ctxRef.current
          const ids = [...droppableContainers.keys()]
          if (ids.length > 0) {
            measureDroppableContainers(ids)
          }
          // Give React time to process the measurement queue.
          // Only THEN clear the ref so collision resumes with fresh rects.
          setTimeout(() => {
            isOverSidebarRef.current = false
          }, 50)
        })
      }, 230) // > 200ms CSS transition duration
    }

    document.addEventListener(DND_SIDEBAR_EXIT_EVENT, handleSidebarExit)
    return () => {
      document.removeEventListener(DND_SIDEBAR_EXIT_EVENT, handleSidebarExit)
      if (expandTimer) clearTimeout(expandTimer)
    }
  }, [])

  return null
}
